<template>
  <div class="chat-legal-consents">
    <p class="text-subtitle-2 font-weight-bold mb-3">
      {{ t('onboarding.wizard.review.acceptances-intro') }}
    </p>

    <div
      v-if="requiresAgb"
      class="mb-3"
    >
      <v-checkbox
        v-model="acceptAgb"
        class="chat-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="busy || submitting"
      >
        <template #label>
          <span class="text-body-2 chat-legal-consents__label">
            {{ t('onboarding.submit.acceptTerms') }}
            <a
              v-if="publicTOCandLicenceUrl"
              class="text-primary"
              target="_blank"
              rel="noopener noreferrer"
              :href="publicTOCandLicenceUrl"
              @click.stop
            >
              {{ t('onboarding.submit.termsOf', { company: legalCompanyName }) }}
            </a>
          </span>
        </template>
      </v-checkbox>
    </div>

    <!-- US accepts the privacy policy with the signup consents. -->
    <div
      v-if="!isUsOnboarding"
      class="mb-3"
    >
      <v-checkbox
        v-model="acceptPrivacy"
        class="chat-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="busy || submitting"
      >
        <template #label>
          <span class="text-body-2 chat-legal-consents__label">
            {{ t('onboarding.submit.acceptPrivacy') }}
            <a
              v-if="dataPrivacyUrl"
              class="text-primary"
              target="_blank"
              rel="noopener noreferrer"
              :href="dataPrivacyUrl"
              @click.stop
            >
              {{ t('onboarding.submit.privacyPolicyOf', { company: legalCompanyName }) }}
            </a>
            <span v-else>
              {{ t('onboarding.submit.privacyPolicyOf', { company: legalCompanyName }) }}
            </span>
            <template v-if="privacyEndSuffix"> {{ '\u00a0' }}{{ privacyEndSuffix }} </template>
          </span>
        </template>
      </v-checkbox>
    </div>

    <div
      v-if="requiresGuideline"
      class="mb-3"
    >
      <v-checkbox
        v-model="acceptGuideline"
        class="chat-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="busy || submitting"
      >
        <template #label>
          <span class="text-body-2 chat-legal-consents__label">
            {{ t('onboarding.submit.acceptGuideline') }}
            <a
              v-if="guidelineUrl"
              class="text-primary"
              :href="guidelineUrl"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              {{ t('onboarding.submit.guidelineFor', { platform: readablePlatformName }) }}
            </a>
            <span v-else>
              {{ t('onboarding.submit.guidelineFor', { platform: readablePlatformName }) }}
            </span>
            <template v-if="guidelineReceivedSuffix">
              {{ '\u00a0' }}{{ guidelineReceivedSuffix }}
            </template>
          </span>
        </template>
      </v-checkbox>
    </div>

    <div
      v-if="isUsOnboarding"
      class="mb-3"
    >
      <v-checkbox
        v-model="authorizeSendOnBehalf"
        class="chat-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="busy || submitting"
      >
        <template #label>
          <span class="text-body-2 chat-legal-consents__label">
            {{ t('onboarding.submit.authorizeSendOnBehalf') }}
          </span>
        </template>
      </v-checkbox>
    </div>

    <div class="mb-3">
      <v-checkbox
        v-model="contactViaEmail"
        class="chat-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="busy || submitting"
      >
        <template #label>
          <span class="text-body-2 chat-legal-consents__label">{{
            t('onboarding.submit.contactViaEmail')
          }}</span>
        </template>
      </v-checkbox>
    </div>

    <div class="mb-3">
      <v-checkbox
        v-model="contactViaWhatsapp"
        class="chat-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="busy || submitting"
      >
        <template #label>
          <span class="text-body-2 chat-legal-consents__label">{{
            t('onboarding.submit.contactViaWhatsapp')
          }}</span>
        </template>
      </v-checkbox>
    </div>

    <v-alert
      v-if="showSupportHint"
      class="mb-3"
      type="info"
      variant="tonal"
      density="comfortable"
    >
      <template #title>
        <span class="text-body-2 font-weight-bold">
          {{ t('onboarding.submit.supportAndTipsTitle') }}
        </span>
      </template>
      <template #text>
        <span class="text-body-2">
          {{ t('onboarding.submit.supportAndTipsDescription') }}
        </span>
      </template>
      <template #prepend />
    </v-alert>

    <div class="mb-4">
      <v-checkbox
        v-model="acceptConfirmData"
        class="chat-legal-consents__checkbox"
        color="primary"
        density="comfortable"
        hide-details
        :disabled="busy || submitting"
      >
        <template #label>
          <span class="text-body-2 chat-legal-consents__label">{{
            t('onboarding.submit.confirmAllData')
          }}</span>
        </template>
      </v-checkbox>
    </div>

    <v-btn
      color="primary"
      rounded="lg"
      :disabled="!canConfirm || busy"
      :loading="submitting"
      @click="confirm"
    >
      {{ t('onboarding.wizard.chat-widgets.legal-consents-confirm') }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import { useApolloClient } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useBranding } from '@/composables/useBranding'
import useFeature from '@/composables/useFeature'
import featureNames from '@/lib/featureNames'
import { getMaterialGuideUrl } from '@/lib/materialGuideUrl.js'
import { persistLegalConsents } from '@/modules/onboarding/legalConsentsPersistence'

export type LegalConsentsAnswers = {
  acceptAgb: boolean
  acceptPrivacy: boolean
  acceptGuideline: boolean
  authorizeSendOnBehalf: boolean
  contactViaEmail: boolean
  contactViaWhatsapp: boolean
  acceptConfirmData: boolean
}

const props = defineProps<{
  companyId?: string | null
  busy?: boolean
  /** When true, AGB was already accepted on the company — hide that checkbox. */
  licenseAlreadyAccepted?: boolean
  /** US: send-on-behalf authorization replaces the privacy acknowledgement. */
  isUsOnboarding?: boolean
}>()

const emit = defineEmits<{
  confirm: [answers: LegalConsentsAnswers]
}>()

const { t, te, locale } = useI18n()
const { client } = useApolloClient()
const { isFeatureActive, getFeature } = useFeature()
const {
  legalCompanyName,
  readablePlatformName,
  dataPrivacyUrl,
  publicTOCandLicenceUrl,
  isPlatformSmg,
} = useBranding()

const acceptAgb = ref(false)
const acceptPrivacy = ref(false)
const acceptGuideline = ref(false)
const authorizeSendOnBehalf = ref(false)
const contactViaEmail = ref(false)
const contactViaWhatsapp = ref(false)
const acceptConfirmData = ref(false)
const submitting = ref(false)

const requiresAgb = computed(() => {
  if (props.licenseAlreadyAccepted) return false
  if (!isFeatureActive(featureNames.DATA_PRIVACY)) return false
  const feature = getFeature(featureNames.DATA_PRIVACY)
  return !!(feature?.config?.hasConditions ?? feature?.hasConditions)
})

const requiresGuideline = computed(() => isFeatureActive(featureNames.MATERIAL_GUIDE))

const guidelineUrl = computed(() => {
  if (!requiresGuideline.value) return null
  const feature = getFeature(featureNames.MATERIAL_GUIDE)
  if (!feature?.config) return null
  return getMaterialGuideUrl({
    config: feature.config,
    locale: locale.value,
    isSmg: !!isPlatformSmg.value,
  })
})

const guidelineReceivedSuffix = computed(() => {
  // EN leaves this empty ("I have received the …"); DE uses "erhalten".
  // `te` checks the active locale only, so a missing key can't fall back to DE "erhalten".
  if (!te('onboarding.submit.received')) return ''
  const s = t('onboarding.submit.received')
  return typeof s === 'string' && s.trim() ? s.trim() : ''
})

const privacyEndSuffix = computed(() => {
  if (!te('onboarding.submit.acceptPrivacyEnd')) return ''
  const s = t('onboarding.submit.acceptPrivacyEnd')
  return typeof s === 'string' && s.trim() ? s.trim() : ''
})

const canConfirm = computed(() => {
  const agbOk = !requiresAgb.value || acceptAgb.value
  const guidelineOk = !requiresGuideline.value || acceptGuideline.value
  const marketOk = props.isUsOnboarding ? authorizeSendOnBehalf.value : acceptPrivacy.value
  return agbOk && marketOk && guidelineOk && acceptConfirmData.value
})

const showSupportHint = computed(
  () => canConfirm.value && !contactViaEmail.value && !contactViaWhatsapp.value
)

const confirm = async () => {
  if (!canConfirm.value || submitting.value) return
  submitting.value = true
  try {
    await persistLegalConsents({
      mutate: (options) => client.mutate(options),
      companyId: props.companyId,
      values: {
        acceptAgb: acceptAgb.value,
        acceptPrivacy: acceptPrivacy.value,
        acceptGuideline: acceptGuideline.value,
        authorizeSendOnBehalf: authorizeSendOnBehalf.value,
        contactViaEmail: contactViaEmail.value,
        contactViaWhatsapp: contactViaWhatsapp.value,
        acceptConfirmData: acceptConfirmData.value,
      },
      requiresAgb: requiresAgb.value,
      requiresGuideline: requiresGuideline.value,
      isUsOnboarding: Boolean(props.isUsOnboarding),
      proofTextContext: {
        t,
        legalCompanyName: legalCompanyName.value,
        readablePlatformName: readablePlatformName.value,
        publicTOCandLicenceUrl: publicTOCandLicenceUrl.value,
      },
    })
    emit('confirm', {
      acceptAgb: acceptAgb.value,
      acceptPrivacy: acceptPrivacy.value,
      acceptGuideline: acceptGuideline.value,
      authorizeSendOnBehalf: authorizeSendOnBehalf.value,
      contactViaEmail: contactViaEmail.value,
      contactViaWhatsapp: contactViaWhatsapp.value,
      acceptConfirmData: acceptConfirmData.value,
    })
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.chat-legal-consents {
  max-width: 100%;
}

.chat-legal-consents__label {
  opacity: 1;
  line-height: 1.4;
}

.chat-legal-consents__checkbox :deep(.v-label) {
  opacity: 1;
}
</style>
